/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import { CacheType, createObservableDataAction, IAction, IActionContext, IActionInput } from '@msdyn365-commerce/core';

/**
 * OneRF profile update response interface
 */
export interface IOneRFProfileUpdateResponse {
    success: boolean;
    message?: string;
    errors?: string[];
}

/**
 * Input class for the updateProfileOneRF data action
 */
export class UpdateProfileOneRFInput implements IActionInput {
    public readonly customerAccountNumber: string;
    public readonly givenName: string;
    public readonly surname: string;
    public readonly mail?: string;

    public constructor(customerAccountNumber: string, givenName: string, surname: string, displayName?: string, mail?: string) {
        this.customerAccountNumber = customerAccountNumber;
        this.givenName = givenName;
        this.surname = surname;
        this.mail = mail;
    }

    /**
     * Get Cache Key.
     * @returns - Cache key string.
     */
    public getCacheKey = (): string => {
        return `onerf-profile-update-${this.givenName}-${this.surname}-${this.customerAccountNumber}`;
    };

    /**
     * Get Cache Object Type.
     * @returns - Cache object type string.
     */
    public getCacheObjectType = (): string => 'OneRFProfileUpdate';

    /**
     * Get Cache Type.
     * @returns - CacheType enum.
     */
    public dataCacheType = (): CacheType => 'none';
}

/**
 * The action method for the updateProfileOneRF data action
 * @param input The action input containing profile payload
 * @param ctx The action context
 */
export async function updateProfileOneRFAction(input: UpdateProfileOneRFInput, ctx: IActionContext): Promise<IOneRFProfileUpdateResponse> {
    const { givenName, surname, mail } = input;

    try {
        // Construct OneRF endpoint URL
        const oneRFEndpoint = `/onerf/_msdyn365/editexternalidprofile`;

        // Prepare request headers with Bearer token authentication
        const headers: Record<string, string> = {
            'Content-Type': 'application/json'
        };

        // Add Bearer token authentication
        if (!ctx.requestContext.user?.token) {
            ctx.trace('[updateProfileOneRFAction] User is not authenticated');
            return {
                success: false,
                message: 'Authentication required'
            };
        }

        const payload = {
            ...(givenName && { givenName }), // If givenName exists, spread { givenName: "John" }
            ...(surname && { surname }), // If surname exists, spread { surname: "Doe" }
            ...(mail && { mail }) // If mail exists, spread { mail: "john@email.com" }
        };
        headers.Authorization = `Bearer ${ctx.requestContext.user.token}`;

        // Make the HTTP PATCH request to OneRF endpoint
        const fetchResponse = await fetch(oneRFEndpoint, {
            method: 'PATCH',
            headers,
            body: JSON.stringify(payload)
        });

        if (fetchResponse.ok) {
            return {
                success: true,
                message: 'Profile updated successfully'
            };
        } else {
            ctx.trace(`[updateProfileOneRFAction] OneRF request failed with status ${fetchResponse.status}`);
            return {
                success: false,
                message: `Request failed with status ${fetchResponse.status}`
            };
        }
    } catch (error) {
        const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
        ctx.telemetry.error(`[update-profile-onerf] ${errorMessage}`);

        return {
            success: false,
            message: `OneRF profile update failed: ${errorMessage}`,
            errors: [errorMessage]
        };
    }
}

/**
 * The updateProfileOneRF data action
 * Returns the OneRF profile update response
 */
export const updateProfileOneRFDataAction = createObservableDataAction<IOneRFProfileUpdateResponse>({
    id: '@msdyn365-commerce-modules/retail-actions/update-profile-onerf',
    action: <IAction<IOneRFProfileUpdateResponse>>updateProfileOneRFAction
});

export default updateProfileOneRFDataAction;
